import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Grid,
  Header,
  Image,
  Icon,
  Input,
  Label,
  Modal,
  Segment,
} from "semantic-ui-react";

import { CardUploadImagePackage } from "react-lib/apps/HISV3/HCU/CardUploadImagePackage";

import {
  MasterOptionsType,
  PackageDetailType,
  PackageImage,
} from "react-lib/apps/HISV3/HCU/sequence/SettingPackage";

// Styles
import "./CardSettingDisplayPackage.scss";

type CardSettingDisplayPackageProps = {
  //
  runSequence: any;
  setProp: any;
  onEvent: any;
  errorMessage?: Record<string, any>;
  buttonLoadCheck?: Record<string, any>;
  masterOptions?: MasterOptionsType;
  masterData?: Record<string, any>;

  packageImage?: PackageImage[];
  packageDetail?: PackageDetailType;
  priceTotal?: number;

  // Common
  languageUX?: string;
};

type Category = {
  key: string;
  checkboxName: keyof PackageDetailType;
  seqName: keyof PackageDetailType;
  label: string;
};

const categories: Category[] = [
  {
    key: "special",
    checkboxName: "is_special_deal",
    seqName: "special_deal_display_seq",
    label: "ข้อเสนอพิเศษ",
  },
  {
    key: "popular",
    checkboxName: "is_popular",
    seqName: "popular_display_seq",
    label: "รายการตรวจยอดนิยม",
  },
];

const CardSettingDisplayPackage = (props: CardSettingDisplayPackageProps) => {
  const thumbnailRef = useRef<HTMLInputElement>(null);
  const [selectImage, setSelectImage] = useState<number>(1);

  const handleChangeValue = (e: any, data: any) => {
    const value = typeof data.checked === "boolean" ? data.checked : data.value;

    const detail = { ...props.packageDetail };
    (detail as any)[data.name] = value;

    props.setProp("SettingPackageSequence.packageDetail", {
      ...detail,
    });
  };

  const handleToggleHealthConcern = (value: number) => {
    const current = props.packageDetail?.health_concerns ?? [];

    const health_concerns = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];

    handleChangeValue(null, {
      name: "health_concerns",
      value: health_concerns,
    });
  };

  /**
   * รูปที่เลือกอยู่
   * index 1 = รูปหลัก / Thumbnail
   * index 2-4 = รูปรายละเอียด
   */

  return (
    <div className="card-setting-display-package">
      <Segment className="marketing-container">
        {/* Header */}
        <div className="marketing-header">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: "var(--primary-theme-color-dark)",
            }}
          >
            <Icon name="bullhorn" size="large" />

            <div>
              <div
                style={{
                  fontSize: "1.15rem",
                  fontWeight: "bold",
                  marginBottom: "8px",
                }}
              >
                การแสดงผลบนแอป (Marketing Display)
              </div>

              <div style={{ fontSize: "12px" }}>
                ตั้งค่าว่าแพ็กเกจนี้จะแสดงอย่างไรในหน้าแอปของลูกค้า
              </div>
            </div>
          </div>
        </div>

        <Grid divided>
          {/* Left */}
          <Grid.Column width={11}>
            <Form>
              {/* =========================
                  Upload Image
              ========================= */}
              <Form.Group>
                <Form.Field width={16}>
                  <CardUploadImagePackage
                    packageImage={props.packageImage || []}
                    selectImage={selectImage}
                    fileInputRef={thumbnailRef}
                    onChange={(updatedPackageImage) => {
                      props.setProp("SettingPackageSequence.packageImage", updatedPackageImage);
                    }}
                  />
                </Form.Field>
              </Form.Group>

              {/* =========================
                  Select Image
              ========================= */}
              <Form.Group>
                {[1, 2, 3, 4].map((imageNumber) => {
                  const image = props.packageImage?.[imageNumber - 1];

                  return (
                    <Form.Field width={4} key={imageNumber}>
                      <div
                        className={`selection-image-box ${
                          selectImage === imageNumber ? "active" : ""
                        }`}
                        onClick={() => setSelectImage(imageNumber)}
                      >
                        <div className="title">
                          {imageNumber === 1 ? "Thumbnail" : "รายละเอียด"}
                        </div>

                        {image?.thumbnail || image?.image ? (
                          <img
                            src={image.thumbnail || image.image}
                            alt={`รูปที่ ${imageNumber}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "12px",
                            }}
                          />
                        ) : (
                          <>
                            <Icon name="image outline" size="large" />

                            <div className="image-number">รูปที่ {imageNumber}</div>
                          </>
                        )}
                      </div>
                    </Form.Field>
                  );
                })}
              </Form.Group>

              <Form.Group style={{ marginBottom: "10px" }}>
                <Form.Field width={12}>
                  <div
                    style={{
                      fontWeight: "bold",
                      marginBottom: "8px",
                    }}
                  >
                    <label>แสดงในหมวด</label>
                  </div>

                  {categories.map(({ key, checkboxName, label }) => {
                    const checked = !!props.packageDetail?.[checkboxName];

                    return (
                      <div key={key} style={{ marginBottom: "10px" }}>
                        <Button
                          className={`categories-button ${checked ? "active" : ""}`}
                          type="button"
                          onClick={() =>
                            handleChangeValue(null, {
                              name: checkboxName,
                              checked: !checked,
                            })
                          }
                        >
                          <Checkbox
                            className={`categories-checkbox ${checked ? "active" : ""}`}
                            checked={checked}
                            style={{
                              pointerEvents: "none",
                            }}
                          />

                          <label
                            style={{
                              fontSize: 13,
                              fontWeight: "bold",
                              color: checked
                                ? "var(--primary-theme-color-dark)"
                                : "rgb(106, 95, 99)",
                            }}
                          >
                            {label}
                          </label>
                        </Button>
                      </div>
                    );
                  })}
                </Form.Field>

                <Form.Field width={4}>
                  <div
                    style={{
                      fontWeight: "bold",
                      marginBottom: "8px",
                    }}
                  >
                    <label>ลำดับแสดง</label>
                  </div>

                  {categories.map(({ key, seqName }) => (
                    <div key={key} style={{ marginBottom: "10px" }}>
                      <Input
                        className="categories-seq-input"
                        type="number"
                        name={seqName}
                        value={props.packageDetail?.[seqName] ?? ""}
                        onChange={handleChangeValue}
                      />
                    </div>
                  ))}
                </Form.Field>
              </Form.Group>

              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Field width={12}>
                  <div
                    style={{
                      display: "flex",
                      fontWeight: "bold",
                      marginBottom: "8px",
                    }}
                  >
                    <label>คำโปรยสั้น</label>
                    <label style={{ color: "gray" }}>(ใต้ชื่อบนการ์ด)</label>
                  </div>

                  <Input
                    className="categories-input"
                    type="text"
                    name="short_description"
                    value={props.packageDetail?.short_description}
                    onChange={handleChangeValue}
                  />
                </Form.Field>
                <Form.Field width={4}>
                  <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                    <label>จำนวนรายการตรวจ</label>
                  </div>

                  <div>
                    <Input
                      className="categories-input"
                      type="number"
                      name="display_items_count"
                      value={props.packageDetail?.display_items_count}
                      onChange={handleChangeValue}
                    />
                  </div>
                </Form.Field>
              </Form.Group>

              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Field width={16}>
                  <div
                    style={{
                      display: "flex",
                      fontWeight: "bold",
                      marginBottom: "8px",
                    }}
                  >
                    <label>เหมาะสำหรับ</label>
                    <label style={{ color: "gray" }}>(แท็กช่วยแนะนำในแบบสอบถาม)</label>
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {(props.masterOptions?.healthConcernList || []).map((item) => {
                      const active =
                        props.packageDetail?.health_concerns?.includes(item.value) ?? false;

                      return (
                        <Button
                          key={item.key}
                          type="button"
                          className={`tag-button ${active ? "active" : ""}`}
                          onClick={() => handleToggleHealthConcern(item.value)}
                        >
                          {item.text}
                        </Button>
                      );
                    })}
                  </div>
                </Form.Field>
              </Form.Group>
            </Form>
          </Grid.Column>

          {/* Right */}
          <Grid.Column width={5}>
            <PackagePreview
              data={props.packageDetail}
              priceTotal={props.priceTotal}
              packageImage={props.packageImage}
            />
          </Grid.Column>
        </Grid>
      </Segment>
    </div>
  );
};

export default memo(CardSettingDisplayPackage);

type PackagePreviewProps = {
  data?: PackageDetailType;
  priceTotal?: number;
  packageImage?: PackageImage[];
};

export const PackagePreview = ({ data, priceTotal, packageImage }: PackagePreviewProps) => {
  const [currentImage, setCurrentImage] = useState(0);

  return (
    <>
      <div className="preview-title">ตัวอย่างการ์ดบนแอป</div>

      <div className="preview-card">
        <div className="preview-image-wrapper">
          {packageImage?.[0]?.thumbnail ? (
            <img src={packageImage?.[0]?.thumbnail || ""} className="preview-image" />
          ) : (
            <div className="preview-image empty" />
          )}
        </div>

        <div className="preview-content">
          <div className="preview-title">{data?.name}</div>

          <div className="preview-description">{data?.short_description}</div>

          <div className="preview-items">
            <Icon name="check square outline" />
            <span>{data?.display_items_count || 0} รายการตรวจ</span>
          </div>

          <div className="preview-price-group">
            <span className="preview-price">{(data?.price_normal || 0).toLocaleString()}</span>

            <span className="preview-unit">บาท</span>

            {!!data?.is_special_deal && (
              <span className="preview-old-price">{(priceTotal || 0).toLocaleString()}</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: "14px" }}>
        {categories
          .filter(({ checkboxName }) => !!data?.[checkboxName])
          .map(({ key, label }) => (
            <Label key={key} className="label-categories">
              {label}
            </Label>
          ))}
      </div>

      <div
        style={{
          margin: "14px 0px 28px 0px",
          borderBottom: "1px solid var(--primary-theme-color-dark)",
        }}
      ></div>

      <div className="preview-title">ตัวอย่างหน้ารายละเอียด</div>

      <div className="preview-card">
        <div className="preview-image-wrapper">
          {packageImage?.[currentImage]?.thumbnail ? (
            <img
              src={packageImage?.[currentImage]?.thumbnail}
              alt={`รูปที่ ${currentImage + 1}`}
              className="preview-image"
            />
          ) : (
            <div className="preview-image empty" />
          )}
          <div className="preview-package-image-lable">
            <Icon name="square outline" />
            {`${currentImage +1}/4`}
          </div>
        </div>

        <div className="preview-content">
          <div className="preview-title">{data?.name}</div>

          <div style={{ display: "flex", gap: 5 }}>
            {[1, 2, 3, 4]?.map((_, index) => (
              <div
                className={`preview-package-image ${currentImage === index ? "active" : ""}`}
                onClick={() => setCurrentImage(index)}
              ></div>
            ))}
          </div>

          <div className="preview-description">
            ผู้ใช้ปัดซ้าย–ขวาเพื่อดูรูปถัดไป และกดที่รูปเพื่อเปิดแบบเต็มจอ
          </div>
        </div>
      </div>
    </>
  );
};
